import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, CheckCircle2, ClipboardCheck, Copy, ExternalLink, MapPin, Play } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { useNewestRecordFlash } from "@/hooks/useNewestRecordFlash";
import { formatBusinessDate, todayInCentralTime } from "@/utils/datetime";
import { newestFirst } from "@/utils/sortHistory";

const TEXAS811_LOOKUP_URL = "https://txgc.texas811.org/ui/dashboard";

const writeClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    throw new Error("clipboard-unavailable");
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch {
      /* no-op: clipboard blocked in this context */
    }
  }
};

const copyTicketNumber = async (value) => {
  const text = String(value ?? "");
  if (!text) return;
  await writeClipboard(text);
  toast.success(`Copied ticket ${text}`);
};

const open811Lookup = async (value) => {
  const text = String(value ?? "");
  if (!text) return;
  await writeClipboard(text);
  toast.success(`Copied ticket ${text} — opening Texas811`);
  window.open(TEXAS811_LOOKUP_URL, "_blank", "noopener");
};
import BoardLoading from "@/components/BoardLoading";
import BoardWorkspace from "@/components/BoardWorkspace";
import { InformationalStatusText } from "@/components/InformationalStatusText";
import OfficialStatusBadge from "@/components/OfficialStatusBadge";
import { ProjectActionCenter } from "@/components/ProjectActionCenter";
import { ProjectBillingPanel } from "@/components/ProjectBillingPanel";
import { InvoiceEditorDialog } from "@/components/InvoiceEditorDialog";
import { ProjectCommandSummary } from "@/components/ProjectCommandSummary";
import { ProjectCommunicationCenter } from "@/components/ProjectCommunicationCenter";
import { GeneralProjectNotesPanel } from "@/components/GeneralProjectNotesPanel";
import { ProjectEditPanel } from "@/components/ProjectEditPanel";
import { ProjectDetailsPanel } from "@/components/ProjectDetailsPanel";
import { ProjectAppointmentDrawer } from "@/components/ProjectAppointmentDrawer";
import { ProjectCommunicationDrawer } from "@/components/ProjectCommunicationDrawer";
import { ProjectActiveJobDrawer } from "@/components/ProjectActiveJobDrawer";
import ProjectPhotosPanel from "@/components/ProjectPhotosPanel";
import ProjectDocumentsPanel from "@/components/ProjectDocumentsPanel";
import { ProjectPermitLocateDrawer } from "@/components/ProjectPermitLocateDrawer";
import { ProjectWaterLocatePanel } from "@/components/ProjectWaterLocatePanel";
import { ProjectTransferPanel } from "@/components/ProjectTransferPanel";
import { companyRoleLabels } from "@/utils/companyLabels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const stateLabel = (value) => {
  return (value || "Not set").replaceAll("_", " ");
};
const displayDate = (value) => (value ? formatBusinessDate(value) : "Not scheduled");
const displayRecordedDate = (value) => (value ? formatBusinessDate(value) : "Not recorded");
const booleanLabel = (value) => (value ? "Yes" : "No");

export default function ProjectAcceptancePage() {
  const { projectId } = useParams();
  const location = useLocation();
  const {
    canAccessBilling,
    canCreateFieldNotes,
    canManageActiveJobs,
    canManageRecords,
    canManageProjectWorkflow,
  } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [runningAction, setRunningAction] = useState("");
  const [appointmentEditor, setAppointmentEditor] = useState(undefined);
  const [communicationEditor, setCommunicationEditor] = useState(undefined);
  const [invoiceEditor, setInvoiceEditor] = useState(undefined);
  const [activeJobEditor, setActiveJobEditor] = useState(undefined);
  const [permitLocateEditor, setPermitLocateEditor] = useState(undefined);

  const loadProject = useCallback(async () => {
    try {
      setError("");
      const response = await axios.get(`${API}/projects/${projectId}`);
      setData(response.data);
      const people = await axios.get(`${API}/assignable-users`);
      setUsers(people.data.users);
    } catch {
      setError(
        "This project record could not be loaded. Return to the board and try again.",
      );
    }
  }, [projectId]);
  const permitGroups = useMemo(() => {
    const permits = data?.permits || [];
    const isPermit = (record) => record.permit_type === "Permit";
    const isActive = (record) => record.active !== false;
    // Locate/811: CURRENT vs HISTORICAL is derived from expiration (never a
    // stored flag). An expired locate is historical for display even though its
    // record stays active; its original status is preserved. Undated locates
    // stay current. Permits are unchanged (grouped by the active flag only).
    const todayIso = new Date().toISOString().slice(0, 10);
    const isExpiredLocate = (record) => {
      const raw = (record.expires_on || "").trim();
      return raw !== "" && raw < todayIso;
    };
    const isCurrentLocate = (record) =>
      !isPermit(record) && isActive(record) && !isExpiredLocate(record);
    const isHistoricalLocate = (record) =>
      !isPermit(record) && (!isActive(record) || isExpiredLocate(record));
    return {
      activeLocates: newestFirst(permits.filter(isCurrentLocate)),
      activePermits: newestFirst(
        permits.filter((record) => isPermit(record) && isActive(record)),
      ),
      historicalLocates: newestFirst(permits.filter(isHistoricalLocate)),
      historicalPermits: newestFirst(
        permits.filter((record) => isPermit(record) && !isActive(record)),
      ),
    };
  }, [data?.permits]);
  const flashLocateId = useNewestRecordFlash(permitGroups.activeLocates);
  const flashPermitId = useNewestRecordFlash(permitGroups.activePermits);
  const actionCenterActions = useMemo(() => {
    return {
      available: data?.available_actions || [],
      nextRequired: data?.project?.next_required_action,
    };
  }, [data?.available_actions, data?.project?.next_required_action]);
  const invoiceProjects = useMemo(
    () => (data?.project ? [data.project] : []),
    [data?.project],
  );
  const runProjectAction = useCallback(async (action) => {
    setRunningAction(action.id);
    try {
      await axios.post(`${API}/projects/${projectId}/actions/${action.id}`);
      toast.success(`${action.label} recorded and audited.`);
      await loadProject();
    } catch (actionError) {
      toast.error(
        actionError.response?.data?.detail || "This action could not be completed.",
      );
    } finally {
      setRunningAction("");
    }
  }, [loadProject, projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    if (!data || location.hash !== "#general-project-notes") {
      return;
    }
    const timer = setTimeout(() => {
      document
        .getElementById("general-project-notes")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(timer);
  }, [data, location.hash]);

  if (!data && !error) return <BoardLoading label="Loading project record" />;

  const returnTarget =
    location.state?.from === "manager"
      ? "/manager-board"
      : "/master-project-board";
  const returnLabel =
    location.state?.from === "manager"
      ? "Manager Board"
      : "Master Project Board";

  if (error) {
    return (
      <BoardWorkspace
        eyebrow="Project record"
        title="Project unavailable"
        subtitle="The linked Project record could not be reached."
        actions={
          <Link
            className="board-refresh-button"
            data-testid="project-detail-return-button"
            to={returnTarget}
          >
            <ArrowLeft size={15} />
            Return to {returnLabel}
          </Link>
        }
      >
        <section
          className="board-error-state"
          data-testid="project-detail-error"
        >
          <p>{error}</p>
          <button
            data-testid="project-detail-retry-button"
            onClick={loadProject}
            type="button"
          >
            Try again
          </button>
        </section>
      </BoardWorkspace>
    );
  }

  const {
    project,
    active_jobs: activeJobs,
    action_queue_items: actionQueueItems = [],
    permit_actions: permitActions = [],
    appointments = [],
    communications,
    invoices = [],
    recent_activity: recentActivity = [],
    billing_summary: billingSummary = {},
  } = data;
  const assignedProjectManagers = (project.project_manager_user_ids || [])
    .map((userId) => users.find((user) => user.id === userId)?.name)
    .filter(Boolean)
    .join(", ");
  const companyLabels = companyRoleLabels(data.company_context);
  const {
    activeLocates,
    activePermits,
    historicalLocates,
    historicalPermits,
  } = permitGroups;

  return (
    <BoardWorkspace
      eyebrow={
        project.project_code === project.name
          ? "Project record"
          : `Project record · ${project.project_code}`
      }
      headingTestId="project-detail-heading"
      title={project.name}
      subtitle={project.client || "Customer unassigned"}
      actions={
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <ProjectPhotosPanel projectId={project.id} />
            <ProjectDocumentsPanel projectId={project.id} />
          </div>
          <Link
            className="board-refresh-button"
            data-testid="project-detail-return-button"
            to={returnTarget}
          >
            <ArrowLeft size={15} />
            Back to {returnLabel}
          </Link>
        </div>
      }
    >
      {project.lifecycle_state === "closed" && (
        <section
          className="project-historical-banner"
          data-testid="project-closed-historical-banner"
        >
          <strong>Historical project record</strong>
          <span>
            Owner/Admin and Office/Admin can reopen or override the Project Stage when needed.
          </span>
        </section>
      )}
      <section className="project-summary-region" data-testid="project-summary-region">
        <ProjectCommandSummary
          companyLabels={companyLabels}
          managers={assignedProjectManagers}
          project={project}
        />
      </section>
      {canManageProjectWorkflow &&
        (project.lifecycle_state !== "closed" || canAccessBilling) && (
          <ProjectEditPanel
            canAccessBilling={canAccessBilling}
            companyContext={data.company_context}
            onSaved={loadProject}
            project={project}
          />
        )}
      <GeneralProjectNotesPanel
        canAdd={canCreateFieldNotes || canManageProjectWorkflow}
        onChanged={loadProject}
        projectId={projectId}
      />
      <section className="project-record-communication" data-testid="project-record-communication">
        <ProjectCommunicationCenter
          canCreateFieldNotes={false}
          communications={communications}
          permitActions={permitActions}
          onCreated={loadProject}
          onManage={canManageProjectWorkflow ? setCommunicationEditor : undefined}
          projectId={projectId}
          recentActivity={recentActivity}
        />
      </section>
      <ProjectLocateSections
        activeLocates={activeLocates}
        canManageRecords={canManageRecords && project.lifecycle_state !== "closed"}
        canManageWaterLocate={
          canManageProjectWorkflow && project.lifecycle_state !== "closed"
        }
        flashRecordId={flashLocateId}
        historicalLocates={historicalLocates}
        onEditRecord={setPermitLocateEditor}
        onNewRecord={(permitType) => setPermitLocateEditor({ permit_type: permitType })}
        onSaved={loadProject}
        project={project}
      />
      <ProjectBillingPanel
        canAccessBilling={canAccessBilling}
        invoices={invoices}
        onEditInvoice={(invoice) => setInvoiceEditor({ mode: "edit", invoice })}
        onNewInvoice={() => setInvoiceEditor({ mode: "create", invoice: null })}
        summary={billingSummary}
      />
      <section className="project-details-section" data-testid="project-more-details-section">
        <div className="project-details-section-heading">
          <span className="operations-kicker">More details</span>
          <h2>Permits, appointments, jobs, and actions</h2>
        </div>
        <section className="project-command-center" data-testid="project-command-center">
          <ProjectActionCenter
            actions={actionCenterActions}
            items={actionQueueItems}
            onRunAction={runProjectAction}
            projectId={projectId}
            runningAction={runningAction}
          />
          <ProjectDetailsPanel
            activeJobs={activeJobs}
            appointments={appointments}
            canManageActiveJobs={
              canManageActiveJobs && project.lifecycle_state !== "closed"
            }
            onEditActiveJob={setActiveJobEditor}
            onEditAppointment={setAppointmentEditor}
            onNewActiveJob={() => setActiveJobEditor(null)}
            onNewAppointment={() => setAppointmentEditor(null)}
          />
          <ProjectPermitSection
            activePermits={activePermits}
            canManageRecords={canManageRecords && project.lifecycle_state !== "closed"}
            flashRecordId={flashPermitId}
            historicalPermits={historicalPermits}
            onEditRecord={setPermitLocateEditor}
            onNewRecord={(permitType) => setPermitLocateEditor({ permit_type: permitType })}
          />
        </section>
      </section>
      {activeJobEditor !== undefined && (
        <ProjectActiveJobDrawer
          canHumanAuthority={canAccessBilling}
          job={activeJobEditor}
          onClose={() => setActiveJobEditor(undefined)}
          onSaved={loadProject}
          project={project}
        />
      )}
      {permitLocateEditor && (
        <ProjectPermitLocateDrawer
          canHumanAuthority={canAccessBilling}
          onClose={() => setPermitLocateEditor(undefined)}
          onSaved={loadProject}
          project={project}
          record={permitLocateEditor}
        />
      )}
      {appointmentEditor !== undefined && (
        <ProjectAppointmentDrawer
          appointment={appointmentEditor}
          onClose={() => setAppointmentEditor(undefined)}
          onSaved={async () => {
            await loadProject();
            setInvoiceEditor(undefined);
          }}
          project={project}
        />
      )}
      {communicationEditor !== undefined && (
        <ProjectCommunicationDrawer
          item={communicationEditor}
          onClose={() => setCommunicationEditor(undefined)}
          onSaved={loadProject}
          project={project}
        />
      )}
      {invoiceEditor !== undefined && (
        <InvoiceEditorDialog
          editor={invoiceEditor}
          lockedProjectId={project.id}
          lockedProjectLabel={`${project.project_code} · ${project.name}`}
          onOpenChange={(open) => !open && setInvoiceEditor(undefined)}
          onSaved={async () => {
            await loadProject();
            setInvoiceEditor(undefined);
          }}
          projects={invoiceProjects}
        />
      )}
      <section className="project-command-secondary" data-testid="project-command-secondary">
        <ProjectCancellationControl
          canAccessBilling={canAccessBilling}
          onCompleted={loadProject}
          project={project}
        />
        <ProjectPriorityControl
          canAccessBilling={canAccessBilling}
          onSaved={loadProject}
          project={project}
        />
      </section>
      <ProjectTransferPanel canTransfer={canAccessBilling} projectId={projectId} />
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function PanelHeading({ icon: Icon, kicker, title }) {
  return (
    <div className="project-panel-heading">
      <Icon size={17} />
      <div>
        <span className="operations-kicker">{kicker}</span>
        <h2>{title}</h2>
      </div>
    </div>
  );
}

function OverviewItem({ icon: Icon, label, testId, value }) {
  return (
    <div className="project-overview-item" data-testid={testId}>
      <Icon size={15} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function FieldValue({ label, testId, value }) {
  return (
    <div className="project-field-value" data-testid={testId}>
      <span>{label}</span>
      <p>{value}</p>
    </div>
  );
}
function ProjectLocateSections({
  activeLocates,
  canManageRecords,
  canManageWaterLocate,
  flashRecordId,
  historicalLocates,
  onEditRecord,
  onNewRecord,
  onSaved,
  project,
}) {
  return (
    <section
      className="project-linked-section project-locate-section"
      data-testid="project-locates-section"
    >
      <div className="project-linked-heading">
        <div>
          <span className="operations-kicker">Linked operating records</span>
          <h2>Locates / 811</h2>
        </div>
        <p>
          Current locate coverage stays visible before downstream Project details.
        </p>
      </div>
      <div className="project-linked-grid">
        <LinkedRecordGroup
          activeRecords={activeLocates}
          flashRecordId={flashRecordId}
          historyRecords={historicalLocates}
          icon={MapPin}
          kind="locate"
          canManageRecords={canManageRecords}
          onEditRecord={onEditRecord}
          onNewRecord={onNewRecord}
          title="Locate / 811"
        />
        <ProjectWaterLocatePanel
          canManageRecords={canManageRecords}
          canSubmitWaterLocate={canManageWaterLocate}
          onSaved={onSaved}
          project={project}
        />
      </div>
    </section>
  );
}

function ProjectPermitSection({
  activePermits,
  canManageRecords,
  flashRecordId,
  historicalPermits,
  onEditRecord,
  onNewRecord,
}) {
  return (
    <section
      className="project-permit-more-details"
      data-testid="project-linked-records"
    >
      <LinkedRecordGroup
        activeRecords={activePermits}
        canManageRecords={canManageRecords}
        flashRecordId={flashRecordId}
        historyRecords={historicalPermits}
        icon={ClipboardCheck}
        kind="permit"
        onEditRecord={onEditRecord}
        onNewRecord={onNewRecord}
        title="Permits"
      />
    </section>
  );
}

function LinkedRecordGroup({
  activeRecords,
  flashRecordId,
  historyRecords,
  icon,
  kind,
  title,
  canManageRecords,
  onEditRecord,
  onNewRecord,
}) {
  return (
    <article
      className="linked-record-panel"
      data-testid={`project-${kind}-records-panel`}
    >
      <PanelHeading icon={icon} kicker="Linked records" title={title} />
      {canManageRecords && (
        <button
          data-testid={`project-add-${kind}-button`}
          onClick={() => onNewRecord(kind === "permit" ? "Permit" : "Locate / 811")}
          type="button"
        >
          Add {kind === "permit" ? "Permit" : "Locate / 811"}
        </button>
      )}
      <LinkedRecordList
        canManageRecords={canManageRecords}
        emptyLabel={`No active ${title.toLowerCase()} are linked to this Project.`}
        flashRecordId={flashRecordId}
        kind={kind}
        onEditRecord={onEditRecord}
        records={activeRecords}
        section="active"
      />
      <div
        className="linked-record-history-heading"
        data-testid={`project-${kind}-history-heading`}
      >
        <span>Historical records</span>
        <small>{historyRecords.length}</small>
      </div>
      <LinkedRecordList
        canManageRecords={false}
        emptyLabel={`No historical ${title.toLowerCase()} are linked to this Project.`}
        kind={kind}
        onEditRecord={onEditRecord}
        records={historyRecords}
        section="history"
      />
    </article>
  );
}

function LinkedRecordList({
  canManageRecords,
  emptyLabel,
  flashRecordId,
  kind,
  onEditRecord,
  records,
  section,
}) {
  if (!records.length) {
    return (
      <EmptyLinkedRecord
        label={emptyLabel}
        testId={`project-${kind}-${section}-empty`}
      />
    );
  }

  return (
    <div className="linked-record-list" data-testid={`project-${kind}-${section}-list`}>
      {records.map((record) => {
        const recordStatus = linkedRecordStatus(record, kind);
        const reference = record.ticket_not_needed
          ? "Ticket not needed"
          : record.reference_number || "No number recorded";
        const isFlashing = flashRecordId && flashRecordId === record.id;

        return (
          <div
            className={`linked-record-row linked-record-detail-row${isFlashing ? " newest-record-flash" : ""}`}
            data-testid={`project-${kind}-${section}-${record.id}`}
            key={record.id}
          >
            <div className="linked-record-detail-copy">
              <strong data-testid={`project-${kind}-number-${record.id}`}>
                {reference}
              </strong>
              {!record.ticket_not_needed && record.reference_number ? (
                (kind === "locate" || /locate/i.test(record.permit_type || "")) ? (
                  <button
                    type="button"
                    className="ticket-copy-btn ticket-811-btn"
                    data-testid={`project-${kind}-lookup-${record.id}`}
                    title="Copy ticket & open Texas811 lookup"
                    onClick={() => open811Lookup(record.reference_number)}
                  >
                    <ExternalLink size={13} />
                    <span>811 lookup</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ticket-copy-btn"
                    data-testid={`project-${kind}-copy-${record.id}`}
                    title="Copy ticket number"
                    onClick={() => copyTicketNumber(record.reference_number)}
                  >
                    <Copy size={13} />
                  </button>
                )
              ) : null}
              <span data-testid={`project-${kind}-type-${record.id}`}>
                {record.permit_type}
              </span>
            </div>
            <div
              className="linked-record-date-grid"
              data-testid={`project-${kind}-dates-${record.id}`}
            >
              <span>
                Requested
                <strong data-testid={`project-${kind}-request-date-${record.id}`}>
                  {displayRecordedDate(record.request_date)}
                </strong>
              </span>
              <span>
                Expires
                <strong data-testid={`project-${kind}-expiration-date-${record.id}`}>
                  {displayRecordedDate(record.expires_on)}
                </strong>
              </span>
            </div>
            <InformationalStatusText
              label={recordStatus.label}
              testId={`project-${kind}-status-${record.id}`}
            />
            {canManageRecords && (
              <button
                className="linked-record-edit-button"
                data-testid={`project-${kind}-edit-${record.id}`}
                onClick={() => onEditRecord(record)}
                type="button"
              >
                Edit
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function linkedRecordStatus(record, kind) {
  if (record.ticket_not_needed) {
    return { label: "Ticket not needed", state: "ready" };
  }
  if (!record.expires_on) {
    return { label: "Expiration required", state: "critical" };
  }
  const centralToday = todayInCentralTime();
  if (record.expires_on < centralToday) {
    return { label: "Expired", state: "critical" };
  }
  if (!["valid", "approved", "expiring"].includes(record.status)) {
    return { label: stateLabel(record.status), state: "critical" };
  }
  if (record.status === "expiring") {
    return { label: "Expiring", state: "warning" };
  }
  return { label: "Valid", state: "ready" };
}
function EmptyLinkedRecord({ label, testId }) {
  return (
    <p className="linked-record-empty" data-testid={testId}>
      {label}
    </p>
  );
}

function ProjectDirectCompletionControl({
  canManageProjectWorkflow,
  onCompleted,
  project,
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);

  if (
    !canManageProjectWorkflow ||
    !["in_progress", "blocked"].includes(project.lifecycle_state)
  ) {
    return null;
  }

  const loadPreview = async () => {
    try {
      const response = await axios.get(
        `${API}/projects/${project.id}/completion-preview`,
      );
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Completion summary could not load.");
    }
  };

  const completeProject = async () => {
    setIsCompleting(true);
    try {
      await axios.post(`${API}/projects/${project.id}/complete`, {
        confirmed: true,
        notes,
      });
      toast.success("Project completion recorded.");
      await onCompleted();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Project could not be completed.",
      );
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="project-direct-complete-control" data-testid="project-direct-complete-control">
      <span className="operations-kicker">Human completion confirmation</span>
      {!preview && (
        <button
          data-testid="project-completion-preview-button"
          onClick={loadPreview}
          type="button"
        >
          Review completion summary
        </button>
      )}
      {preview && (
        <div data-testid="project-completion-preview">
          <p>Active Jobs: {preview.active_jobs.length}</p>
          <p>Open Tasks: {preview.open_tasks.length}</p>
          <p>Remaining Appointments: {preview.appointments.length}</p>
          <p>Warnings: {preview.warnings.join(" · ") || "None"}</p>
        </div>
      )}
      <label htmlFor="project-direct-complete-confirmation">
        <input
          checked={confirmed}
          data-testid="project-direct-complete-confirmation"
          id="project-direct-complete-confirmation"
          onChange={(event) => setConfirmed(event.target.checked)}
          type="checkbox"
        />
        <span>I confirm this Project is officially Complete.</span>
      </label>
      <textarea
        data-testid="project-completion-notes-input"
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Optional completion notes"
        value={notes}
      />
      <button
        className="project-direct-complete-button"
        data-testid="project-direct-complete-button"
        disabled={!confirmed || !preview || isCompleting}
        onClick={completeProject}
        type="button"
      >
        <CheckCircle2 size={15} />
        {isCompleting ? "Completing…" : "Mark Complete"}
      </button>
    </div>
  );
}

function ProjectCancellationControl({ canAccessBilling, onCompleted, project }) {
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  if (!canAccessBilling || ["closed", "canceled"].includes(project.lifecycle_state)) {
    return null;
  }
  const cancelProject = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/projects/${project.id}/cancel`, { reason });
      toast.success("Project canceled. Linked invoice history remains attached.");
      await onCompleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Project could not be canceled.");
    } finally {
      setCancelling(false);
    }
  };
  return (
    <div className="project-cancel-control" data-testid="project-cancel-control">
      <span className="operations-kicker">Project cancellation</span>
      <textarea
        data-testid="project-cancel-reason-input"
        onChange={(event) => setReason(event.target.value)}
        placeholder="Cancellation reason (optional)"
        value={reason}
      />
      <button
        data-testid="project-cancel-button"
        disabled={cancelling}
        onClick={cancelProject}
        type="button"
      >
        {cancelling ? "Canceling…" : "Cancel project"}
      </button>
    </div>
  );
}

function ProjectPriorityControl({ canAccessBilling, onSaved, project }) {
  const [saving, setSaving] = useState(false);
  if (!canAccessBilling) return null;
  const priorityButtonLabel = () => {
    if (saving) {
      return "Updating…";
    }
    if (project.is_top_priority) {
      return "Remove from top priorities";
    }
    return "Add to top priorities";
  };
  const togglePriority = async () => {
    setSaving(true);
    try {
      if (project.is_top_priority) {
        await axios.delete(`${API}/projects/${project.id}/priority`);
      } else {
        await axios.post(`${API}/projects/${project.id}/priority`);
      }
      toast.success(
        project.is_top_priority
          ? "Removed from company top priorities."
          : "Added to company top priorities.",
      );
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Priority could not be updated.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="project-priority-control" data-testid="project-priority-control">
      <span className="operations-kicker">Company priority</span>
      <button
        data-testid="project-priority-toggle-button"
        disabled={saving}
        onClick={togglePriority}
        type="button"
      >
        {priorityButtonLabel()}
      </button>
    </div>
  );
}

function ProjectActionPanel({ actions, onCompleted, projectId }) {
  const [runningAction, setRunningAction] = useState("");
  const runAction = async (action) => {
    setRunningAction(action.id);
    try {
      await axios.post(`${API}/projects/${projectId}/actions/${action.id}`);
      toast.success(`${action.label} recorded and audited.`);
      await onCompleted();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "This action could not be completed.",
      );
    } finally {
      setRunningAction("");
    }
  };
  if (!actions.length) return null;
  return (
    <section
      className="project-action-panel"
      data-testid="project-action-panel"
    >
      <div>
        <span className="operations-kicker">Role-approved actions</span>
        <h2>Project actions</h2>
        <p>
          Only backend-authorized actions for your role and the persisted
          Project state are shown.
        </p>
      </div>
      <div className="project-action-list">
        {actions.map((action) => (
          <button
            data-testid={`project-action-${action.id}`}
            disabled={Boolean(runningAction)}
            key={action.id}
            onClick={() => runAction(action)}
            type="button"
          >
            <span>
              {action.id.includes("submit") ? (
                <Play size={16} />
              ) : (
                <CheckCircle2 size={16} />
              )}
            </span>
            <strong>
              {runningAction === action.id ? "Recording…" : action.label}
            </strong>
            <small>{action.detail}</small>
          </button>
        ))}
      </div>
    </section>
  );
}
